import { Navbar, Sidebar, SidebarMenuItem } from '@southfields-digital/mxmz-react-ui';
import classNames from 'classnames';
import { useContext, useState, useMemo, createContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useSearchParams } from 'react-router-dom';

import DateInput from 'src/components/DateInput';
import GoLive from 'src/components/GoLive';
import LiveClock from 'src/components/LiveClock';
import LiveRundowns from 'src/components/LiveRundowns';
import RundownHeader from 'src/components/RundownHeader/RundownHeader';
import RundownSettings from 'src/components/RundownSettings';
import { menuConfig } from 'src/config/menuConfig';
import useAuth from 'src/hooks/useAuth';
import { StateType } from 'src/redux/reducers';
import { REQUEST_GET_LIVE_RUNDOWNS } from 'src/redux/reducers/rundown';
import { REQUEST_USER_LOGOUT, RoleEnum, UserAuthType } from 'src/redux/reducers/user';
import { REQUEST_SET_CURRENT_WORKSPACE } from 'src/redux/reducers/workspace';

import styles from './BaseLayout.module.scss';
import type { BaseLayoutContextType } from './BaseLayout.types';

export const BaseLayoutContext = createContext<BaseLayoutContextType | null>(null);

const editorFrontendUrl = import.meta.env.VITE_SERVICE_GRAPHICS_EDITOR_URL;
const dashboardFrontendUrl = import.meta.env.VITE_SERVICE_DASHBOARD_URL;
const accountSettingsUrl = import.meta.env.VITE_SERVICE_ACCOUNT_SETTINGS_URL;

const Container = ({
  children,
  withContainer,
}: {
  children: React.ReactNode;
  withContainer: boolean;
}) =>
  !withContainer ? (
    <>{children}</>
  ) : (
    <div className={styles.Container}>
      <div className={styles.ContainerInner}>{children}</div>
    </div>
  );

export default function BaseLayout({
  auth,
  children,
}: {
  auth?: UserAuthType;
  children?: React.ReactNode;
}) {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const isMatchControlLayout = searchParams.get('layout') === 'matchcontrol';

  const { getUserLabel, hasAllRoles, hasAnyRole } = useAuth();

  const liveRundowns = useSelector((state: StateType) => state.rundown.live);
  const workspaces = useSelector((state: StateType) => state.workspace.collection);

  const [currentlyOpenRundownId, setCurrentlyOpenRundownId] = useState<string | undefined>(
    undefined
  );
  const [fullWidth, setFullWidth] = useState(false);
  const [withContainer, setWithContainer] = useState(false);
  const [displayClock, setDisplayClock] = useState(false);
  const [goLive, setGoLive] = useState(false);
  const [showDateinput, setShowDateInput] = useState(false);
  const [showRundownName, setShowRundownName] = useState(false);
  const [showRundowSettings, setShowRundownSettings] = useState(false);

  const showDashboard = useMemo(() => {
    if (!auth) {
      return false;
    }

    return hasAnyRole([RoleEnum.Admin, RoleEnum.Superadmin]);
  }, [auth, hasAnyRole]);

  const showMenuItems = useMemo(() => {
    if (!auth) {
      return false;
    }

    return (
      hasAllRoles([RoleEnum.Superadmin]) ||
      hasAllRoles([RoleEnum.Designer, RoleEnum.Operator]) ||
      hasAllRoles([RoleEnum.Designer, RoleEnum.Admin])
    );
  }, [auth, hasAllRoles]);

  const workspaceOptions = useMemo(
    () => workspaces.data.map(({ id, name }) => ({ id, name })),
    [workspaces]
  );

  const currentWorkspaceId = auth?.workspace?.id;

  const menuItems = menuConfig
    .filter((config) => (config.roles ? hasAnyRole(config.roles) : true))
    .map(
      (menuItem): SidebarMenuItem => ({
        icon: menuItem.icon,
        pathname: menuItem.path,
      })
    );

  return (
    <div className={styles.Root}>
      <BaseLayoutContext.Provider
        value={{
          setCurrentlyOpenRundownId,
          setFullWidth,
          setWithContainer,
          setDisplayClock,
          setGoLive,
          setShowDateInput,
          setShowRundownName,
          setShowRundownSettings,
        }}
      >
        <Navbar
          links={[
            {
              to: '/',
              label: 'Operator',
              isActive: true,
              isHidden: !showMenuItems,
            },
            {
              to: editorFrontendUrl,
              label: 'Designer',
              isActive: false,
              isHidden: !showMenuItems,
            },
          ]}
          isAuthenticated
          className={classNames(styles.Navbar, {
            '!bg-default-surface': isMatchControlLayout,
          })}
          contentCenter={<div>{showRundownName && <RundownHeader />}</div>}
          contentRight={
            <div className="flex items-center gap-x-3">
              {goLive && <GoLive />}
              {displayClock && <LiveClock />}
              {showDateinput && <DateInput />}
              {showRundowSettings && <RundownSettings />}

              <LiveRundowns
                currentlyOpenRundownId={currentlyOpenRundownId}
                liveRundowns={liveRundowns}
                getLiveRundowns={() => dispatch({ type: REQUEST_GET_LIVE_RUNDOWNS })}
              />
            </div>
          }
        />

        <main className={styles.Main}>
          <Sidebar
            avatarInput={auth?.name}
            menuItems={menuItems}
            className={styles.Sidebar}
            currentWorkspaceId={currentWorkspaceId}
            handleDashboard={() => {
              window.location.href = dashboardFrontendUrl;
            }}
            handleAccountSettings={() => {
              window.location.href = accountSettingsUrl;
            }}
            handleLogout={() => dispatch({ type: REQUEST_USER_LOGOUT })}
            isAuthenticated
            setCurrentWorkspace={(workspaceId: string) =>
              dispatch({ type: REQUEST_SET_CURRENT_WORKSPACE, payload: { workspaceId } })
            }
            userLabel={getUserLabel()}
            showDashboard={showDashboard}
            workspaces={workspaceOptions}
          />

          <div
            className={classNames(styles.View, {
              [styles.FullWidthView]: fullWidth,
              [styles.ContainedView]: withContainer,
            })}
          >
            <Container withContainer={withContainer}>{children || <Outlet />}</Container>
          </div>
        </main>
      </BaseLayoutContext.Provider>
    </div>
  );
}

export const injectBaseLayoutContext = (WrappedComponent: React.FC<any>) => {
  const WithBaseLayoutContext = (props: any) => {
    const baseLayoutContext = useContext<BaseLayoutContextType | null>(
      BaseLayoutContext
    ) as BaseLayoutContextType;

    return <WrappedComponent baseLayoutContext={baseLayoutContext} {...props} />;
  };

  return WithBaseLayoutContext;
};

export * from './BaseLayout.types';
